/**
 * Clears a cookie by setting its expiration date to the past.
 * @param name - The name of the cookie to clear.
 * @param domain - Optional domain of the cookie.
 * @param path - Optional path of the cookie (defaults to '/').
 */
export const clearCookie = (
  name: string,
  domain?: string,
  path: string = "/",
) => {
  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
  if (domain) {
    cookieString += ` domain=${domain};`;
  }
  document.cookie = cookieString;
};

/**
 * Clears all cookies that match a given prefix or regular expression.
 * @param pattern - The prefix or regex to match cookie names against.
 */
export const clearCookiesByPattern = (pattern: string | RegExp) => {
  const cookies = document.cookie.split(";");
  const regex =
    typeof pattern === "string" ? new RegExp(`^\\s*${pattern}`) : pattern;

  cookies.forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (regex.test(name)) {
      clearCookie(name);
      // Also try to clear with common subdomains/domains if necessary
      const domainParts = window.location.hostname.split(".");
      while (domainParts.length > 0) {
        const domain = domainParts.join(".");
        clearCookie(name, domain);
        clearCookie(name, `.${domain}`);
        domainParts.shift();
      }
    }
  });
};
/**
 * Clears all localStorage items that match a given prefix or regular expression.
 * @param pattern - The prefix or regex to match localStorage keys against.
 */
export const clearLocalStorageByPattern = (pattern: string | RegExp) => {
  const regex =
    typeof pattern === "string" ? new RegExp(`^\\s*${pattern}`) : pattern;

  Object.keys(localStorage).forEach((key) => {
    if (regex.test(key)) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * Clears all sessionStorage items that match a given prefix or regular expression.
 * @param pattern - The prefix or regex to match sessionStorage keys against.
 */
export const clearSessionStorageByPattern = (pattern: string | RegExp) => {
  const regex =
    typeof pattern === "string" ? new RegExp(`^\\s*${pattern}`) : pattern;

  Object.keys(sessionStorage).forEach((key) => {
    if (regex.test(key)) {
      sessionStorage.removeItem(key);
    }
  });
};

/**
 * Clears all cookies, localStorage, and sessionStorage items matching a pattern.
 * Use this for GDPR-compliant cleanup when a user revokes consent for a vendor.
 * @param pattern - The prefix or regex to match against.
 */
export const clearAllStorageByPattern = (pattern: string | RegExp) => {
  clearCookiesByPattern(pattern);
  clearLocalStorageByPattern(pattern);
  clearSessionStorageByPattern(pattern);
};
